import React, { useMemo } from 'react';

import { Flex, Link } from '@chakra-ui/react';
import { format } from 'date-fns';

import StaticTable from 'components/layouts/StaticTable';
import Button from 'components/primitives/Button';
import Typography from 'components/primitives/Typography';
import { routesSettings } from 'constants/routes';
import getFullPropertyTitle from 'utils/getFullPropertyTitle';
import ucFirst from 'utils/string/ucFirst';

import { MODE } from '../CharityDetails/CharityDetails.props';
import { CharityFullDetailsProps } from './CharityFullDetails.props';

export const columns = ['Property', 'Amount', 'Donated On'];

const CharityFullDetailsView = (
  props: CharityFullDetailsProps
): JSX.Element => {
  const usageSummary = useMemo(
    () =>
      (props.data?.donations.slice(0, 5) || []).map((tr) => [
        <Link
          href={`${routesSettings.MAIN_LISTINGS.path}?id=${tr.property.id}`}
          key={`link:${tr.property.id}`}
        >
          <Typography variant="type10" color="primary" weight="600">
            {getFullPropertyTitle(tr.property.title, tr.property.location)}
          </Typography>
        </Link>,
        `NZD ${tr.amount}`,
        tr.createdAt
          ? format(new Date(tr.createdAt), 'dd/MM/y HH:mm:ss a')
          : '-',
      ]),
    [props.data?.donations]
  );

  return (
    <>
      <Flex
        flexDir="column"
        border="1px solid #ddd"
        p={4}
        flex={4}
        height="100%"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #ddd"
          m={-4}
          px={4}
          py={2}
          mb={4}
        >
          <Typography variant="type8" color="shade5" weight="600">
            CHARITY DETAILS
          </Typography>
          <Button
            variant="small"
            outline
            onClick={() => props.onSetMode(MODE.EDIT)}
          >
            Edit
          </Button>
        </Flex>
        <Flex>
          <Flex flex={1} flexDirection="column">
            <Flex flex={1} flexWrap="wrap">
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  CHARITY NAME
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {props.data.charity.name}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  PRIORITY
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {props.data.charity.priority}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  DONATIONS
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {props.data.charity.donationsCount}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  TOTAL AMOUNT
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  NZD {props.data.charity.donationsTotalAmount}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  STATUS
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {ucFirst(props.data.charity.status)}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  CREATED BY
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {props.data.charity.createdBy.name}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  DATE CREATED
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {props.data.charity.createdAt
                    ? format(
                        new Date(props.data.charity.createdAt),
                        'MM/dd/y kk:mm:ss a'
                      )
                    : '-'}
                </Typography>
              </Flex>
              <Flex flexDirection="column" w="20%" mt={4}>
                <Typography variant="type10" color="shade1" weight="600">
                  LAST UPDATED
                </Typography>
                <Typography
                  variant="type8"
                  color="shade5"
                  style={{ marginTop: 5 }}
                >
                  {props.data.charity.updatedAt
                    ? format(
                        new Date(props.data.charity.updatedAt),
                        'MM/dd/y kk:mm:ss a'
                      )
                    : '-'}
                </Typography>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        flexDir="column"
        border="1px solid #ddd"
        p={4}
        mt={4}
        flex={4}
        height="100%"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #ddd"
          m={-4}
          px={4}
          py={2}
          mb={4}
        >
          <Typography variant="type8" color="shade5" weight="600">
            DONATIONS
          </Typography>
        </Flex>
        <StaticTable columns={columns} data={usageSummary} />
      </Flex>
    </>
  );
};

export default CharityFullDetailsView;
